import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Copy,
  Pencil,
  Plus,
  Settings2,
  Trash2,
  Users,
  DollarSign,
  Calendar,
  ListChecks,
  Check,
  X,
  Lock,
  Trophy,
  AlertTriangle,
  Save,
  FolderOpen,
  ChevronDown,
  ScanLine,
  UserCog,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { SiteHeader } from "@/components/Logo";
import { PboLiveBoard } from "@/components/PboLiveBoard";
import SeatLinkQR from "@/components/SeatLinkQR";
import RollCallView from "@/components/RollCallView";
import ManageMembersDialog from "@/components/ManageMembersDialog";
import { GolferDetailProvider, GolferName } from "@/components/GolferDetailSheet";
import { PboMatchPanel } from "@/components/PboMatchPanel";
import useContestSocket from "@/hooks/useContestSocket";
import {
  fetchContest,
  addContestGolfer,
  editContestGolfer,
  removeContestGolfer,
  updateContestSettings,
  deleteContest,
  submitContestEntry,
  lockContest,
  finalizeContest,
  reopenContestScoring,
  refreshContestScores,
  fetchPboTournaments,
  setTeamName,
} from "@/lib/contestApi";
import {
  getContestSession,
  saveContestSession,
  clearContestSession,
} from "@/lib/contestStorage";
import {
  getContestListNames,
  getContestList,
  saveContestList,
  deleteContestList,
  renameContestList,
  exportContestListsJson,
  importContestListsJson,
} from "@/lib/contestLists";

const fmtMoney = (n) =>
  typeof n === "number" ? `$${n.toLocaleString()}` : "$0";

const fmtLockTime = (iso) => {
  if (!iso) return "No deadline set";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "Invalid date";
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const toDatetimeLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 16);
};

export default function ContestLobby() {
  const { code } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const rejoinPid = searchParams.get("as");
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  const upperCode = (code || "").toUpperCase();

  // Resolve a ?as=<participant_id> deep-link rejoin
  useEffect(() => {
    if (!rejoinPid) return;
    fetchContest(upperCode)
      .then((c) => {
        const member = c.members.find((m) => m.id === rejoinPid);
        if (member) {
          saveContestSession(upperCode, member.id, member.username);
          setSearchParams({}, { replace: true });
          toast.success(`Welcome back, ${member.username}`);
        } else {
          toast.error("Seat link expired");
        }
      })
      .catch(() => toast.error("Couldn't resolve that link"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rejoinPid]);

  const load = useCallback(async () => {
    try {
      const sess = getContestSession(upperCode);
      const c = await fetchContest(upperCode, sess?.participantId);
      setState(c);
      let member = sess
        ? c.members.find((m) => m.id === sess.participantId)
        : null;
      if (!member && c.my_member) {
        // Signed-in user already owns a seat (e.g. host linked their email) —
        // auto-resume it without going through the join form.
        member = c.members.find((m) => m.id === c.my_member.id) || null;
        if (member) {
          saveContestSession(upperCode, member.id, member.username);
          toast.success(`Welcome, ${member.username} — this seat is yours`);
        }
      }
      setMe(member || null);
      if (!member) {
        nav(`/contest/join/${upperCode}`, { replace: true });
      }
    } catch (err) {
      toast.error("Contest not found");
      nav("/", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [upperCode, nav]);

  useEffect(() => {
    load();
  }, [load]);

  // Polling refresh every 5s while in the lobby
  useEffect(() => {
    const id = setInterval(() => {
      const sess = getContestSession(upperCode);
      fetchContest(upperCode, sess?.participantId)
        .then(setState)
        .catch(() => {});
    }, 5000);
    return () => clearInterval(id);
  }, [upperCode]);

  // Instant push from the backend (PBO score stream → us → here)
  const lastLiveRef = useRef(0);
  const [liveFlash, setLiveFlash] = useState(false);
  useContestSocket(upperCode, (msg) => {
    if (msg.type === "deleted") {
      clearContestSession(upperCode);
      toast.error("This contest was deleted by the host");
      nav("/", { replace: true });
      return;
    }
    const sess = getContestSession(upperCode);
    fetchContest(upperCode, sess?.participantId)
      .then((c) => {
        setState(c);
        if (msg.type === "scores_updated" && Date.now() - lastLiveRef.current > 4000) {
          lastLiveRef.current = Date.now();
          setLiveFlash(true);
          setTimeout(() => setLiveFlash(false), 2500);
        }
      })
      .catch(() => {});
  });

  // 5-minute lock warning toast (one-shot)
  const warnedRef = useRef(false);
  useEffect(() => {
    if (!state?.lock_at || state.status !== "open") return;
    const check = () => {
      const ms = new Date(state.lock_at).getTime() - Date.now();
      if (ms > 0 && ms <= 5 * 60 * 1000 && !warnedRef.current) {
        warnedRef.current = true;
        toast.warning("⏰ 5 minutes until picks lock — finalize your team!", {
          duration: 8000,
        });
      }
    };
    check();
    const id = setInterval(check, 15000);
    return () => clearInterval(id);
  }, [state?.lock_at, state?.status]);

  // ----- Pick draft state (lifted; both PickPanel and GolferPool need it) -----
  const [localPicks, setLocalPicks] = useState([]);
  const [editingPicks, setEditingPicks] = useState(false);
  const [submittingPicks, setSubmittingPicks] = useState(false);
  const initRef = useRef(false);

  // First time we see the contest state, decide: edit mode if no submission yet,
  // otherwise show submitted view. Sync localPicks to submitted picks while
  // NOT editing (so background polls don't clobber the user's in-progress draft).
  useEffect(() => {
    if (!state) return;
    if (!initRef.current) {
      initRef.current = true;
      setLocalPicks(state.my_entry?.picks || []);
      setEditingPicks(!state.my_entry && state.status === "open");
      return;
    }
    if (!editingPicks) {
      setLocalPicks(state.my_entry?.picks || []);
    }
  }, [state, editingPicks]);

  const submitPicks = async () => {
    if (!me) return;
    setSubmittingPicks(true);
    try {
      await submitContestEntry(state.code, me.id, localPicks);
      toast.success(
        localPicks.length === state.roster_size
          ? "✓ Lineup submitted"
          : `Picks saved (${localPicks.length} of ${state.roster_size})`
      );
      setEditingPicks(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit picks");
    } finally {
      setSubmittingPicks(false);
    }
  };

  const startEditPicks = () => setEditingPicks(true);
  const cancelEditPicks = () => {
    setLocalPicks(state?.my_entry?.picks || []);
    setEditingPicks(false);
  };

  if (loading || !state) {
    return (
      <div className="min-h-screen bg-pbo-cream">
        <SiteHeader />
        <div className="max-w-5xl mx-auto p-6 text-center text-pbo-muted">
          Loading contest…
        </div>
      </div>
    );
  }

  const isHost = me?.is_host;

  return (
    <GolferDetailProvider code={upperCode} enabled={state.scoring_source === "pbo"}>
    <div className="min-h-screen bg-pbo-cream">
      {liveFlash && (
        <div
          className="fixed top-3 left-1/2 -translate-x-1/2 z-50 px-3 py-1.5 rounded-full bg-pbo-green text-pbo-cream text-xs font-semibold shadow-lg flex items-center gap-2 animate-in fade-in slide-in-from-top-2"
          data-testid="live-update-flash"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-pbo-gold animate-pulse" /> Scores just updated
        </div>
      )}
      <SiteHeader
        rightSlot={
          <button
            onClick={() => {
              clearContestSession(upperCode);
              nav("/");
            }}
            className="text-sm text-pbo-muted hover:text-pbo-green"
            data-testid="contest-leave-btn"
          >
            Leave
          </button>
        }
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          <HeaderCard state={state} me={me} onChanged={load} />

          <StatRow state={state} />

          {state.status === "open" && (
            <PickPanel
              state={state}
              me={me}
              onChanged={load}
              localPicks={localPicks}
              setLocalPicks={setLocalPicks}
              editing={editingPicks}
              submitting={submittingPicks}
              onSubmit={submitPicks}
              onStartEdit={startEditPicks}
              onCancelEdit={cancelEditPicks}
            />
          )}

          {["locked", "final"].includes(state.status) && (
            <LockedTeamsPanel state={state} me={me} onChanged={load} />
          )}

          {state.pbo_live_url && (
            <PboLiveBoard
              url={state.pbo_live_url}
              tournamentName={state.pbo_tournament_name}
              defaultOpen={state.status !== "open"}
            />
          )}

          <GolferPool
            state={state}
            isHost={isHost}
            hostPid={me?.id}
            me={me}
            localPicks={localPicks}
            setLocalPicks={setLocalPicks}
            editingPicks={editingPicks}
            onChanged={load}
          />
        </div>

        {/* Side column */}
        <div className="space-y-6">
          <RosterPanel state={state} me={me} />
        </div>
      </div>
    </div>
    </GolferDetailProvider>
  );
}

const HeaderCard = ({ state, me, onChanged }) => {
  const isHost = me?.is_host;
  const myTeamName = state.members?.find((m) => m.id === me?.id)?.team_name || "";
  const [rollCallOpen, setRollCallOpen] = useState(false);
  const [manageMembersOpen, setManageMembersOpen] = useState(false);
  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`));
  };
  const inviteLink = `${window.location.origin}/contest/join/${state.code}`;
  const seatLink = `${window.location.origin}/contest/${state.code}?as=${me?.id}`;

  const onLockNow = async () => {
    if (!window.confirm("Lock contest now? All picks will be frozen.")) return;
    try {
      await lockContest(state.code, me.id);
      toast.success("Contest locked");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't lock");
    }
  };

  const [refreshing, setRefreshing] = useState(false);
  const onRefreshNow = async () => {
    setRefreshing(true);
    try {
      const res = await refreshContestScores(state.code);
      if (res.ok === false) {
        toast.error(`Sheet error: ${res.error || "unknown"}`);
      } else {
        toast.success("Scores refreshed");
        onChanged();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="rounded-3xl bg-pbo-green text-pbo-cream p-7 relative overflow-hidden">
      <div className="absolute inset-0 pbo-stripe opacity-20 pointer-events-none" />
      <div className="relative">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-pbo-gold font-semibold flex items-center gap-2">
              <span>Contest lobby</span>
              {state.status === "locked" && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-pbo-gold/30 text-pbo-cream text-[10px]">
                  <Lock className="w-3 h-3" /> Locked
                </span>
              )}
              {state.status === "final" && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-pbo-gold text-pbo-ink text-[10px] font-bold"
                  data-testid="final-status-chip"
                >
                  <Trophy className="w-3 h-3" /> Final
                </span>
              )}
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl mt-1">{state.name}</h1>
            {state.scoring_source === "pbo" && (
              <div
                className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full bg-pbo-gold/20 border border-pbo-gold/40 text-pbo-cream text-[11px] font-semibold"
                data-testid="pbo-source-chip"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-pbo-gold animate-pulse" />
                PBO live points · {state.pbo_tournament_name}
              </div>
            )}
            {state.status === "open" && state.lock_at && (
              <LockCountdown lockAt={state.lock_at} />
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isHost && state.has_scoring && (
              <Button
                onClick={onRefreshNow}
                disabled={refreshing}
                variant="ghost"
                className="bg-pbo-cream/10 hover:bg-pbo-cream/20 text-pbo-cream rounded-full h-10"
                data-testid="header-refresh-scores-btn"
              >
                {refreshing
                  ? "Refreshing…"
                  : state.scoring_source === "pbo"
                  ? "🔄 Sync PBO"
                  : "🔄 Sync sheet"}
              </Button>
            )}
            {isHost && state.status === "open" && (
              <Button
                onClick={onLockNow}
                variant="ghost"
                className="bg-pbo-gold/20 hover:bg-pbo-gold/30 text-pbo-cream rounded-full h-10"
                data-testid="lock-now-btn"
              >
                <Lock className="w-4 h-4 mr-1.5" />
                Lock now
              </Button>
            )}
            {isHost && (
              <Button
                onClick={() => setRollCallOpen(true)}
                variant="ghost"
                className="bg-pbo-cream/10 hover:bg-pbo-cream/20 text-pbo-cream rounded-full h-10"
                data-testid="open-roll-call-btn"
              >
                <ScanLine className="w-4 h-4 mr-1.5" />
                Roll-call
              </Button>
            )}
            {isHost && (
              <Button
                onClick={() => setManageMembersOpen(true)}
                variant="ghost"
                className="bg-pbo-cream/10 hover:bg-pbo-cream/20 text-pbo-cream rounded-full h-10"
                data-testid="open-manage-members-btn"
              >
                <UserCog className="w-4 h-4 mr-1.5" />
                Manage members
              </Button>
            )}
            {isHost && (
              <SettingsDialog state={state} hostPid={me.id} onChanged={onChanged} />
            )}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-6 items-end">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-pbo-cream/70 font-semibold">
              Room code
            </div>
            <div className="font-mono text-5xl tracking-[0.3em] text-pbo-gold mt-1" data-testid="contest-room-code">
              {state.code}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={() => copy(state.code, "Code")}
              variant="ghost"
              className="bg-pbo-cream/10 hover:bg-pbo-cream/20 text-pbo-cream rounded-full h-10"
              data-testid="copy-contest-code-btn"
            >
              <Copy className="w-4 h-4 mr-1.5" />
              Copy code
            </Button>
            <Button
              onClick={() => copy(inviteLink, "Invite link")}
              className="bg-pbo-gold text-pbo-ink hover:bg-pbo-gold-soft rounded-full h-10"
              data-testid="copy-contest-invite-btn"
            >
              <Copy className="w-4 h-4 mr-1.5" />
              Copy invite link
            </Button>
          </div>
        </div>

        {me?.id && (
          <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-pbo-cream/80">
            <span className="inline-flex items-center gap-1">
              <Users className="w-3.5 h-3.5" />
              You&apos;re in as <strong className="text-pbo-cream">{me.username}</strong>
              {isHost && (
                <span className="ml-1 px-1.5 py-0.5 rounded bg-pbo-gold/30 text-pbo-gold text-[10px] uppercase tracking-wide font-semibold">
                  Host
                </span>
              )}
            </span>
            <SeatLinkQR
              url={seatLink}
              username={me.username}
              inlineSize={44}
              testId="contest-my-seat-qr"
              variant="dark"
            />
            <button
              onClick={() => copy(seatLink, "Seat link")}
              className="underline hover:text-pbo-gold"
              data-testid="copy-contest-seat-link-btn"
            >
              Copy your seat link
            </button>
          </div>
        )}
        {me?.id && state.status === "open" && (
          <div className="mt-3">
            <TeamNameEditor state={state} me={me} onChanged={onChanged} variant="dark" />
          </div>
        )}
        {me?.id && state.status !== "open" && myTeamName && (
          <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-pbo-gold/40 bg-pbo-gold/10 px-3 py-1.5 text-xs text-pbo-cream/80" data-testid="team-name-locked">
            <Lock className="w-3 h-3 text-pbo-gold" />
            Team: <strong className="text-pbo-gold" data-testid="team-name-display">{myTeamName}</strong>
          </div>
        )}
      </div>

      {isHost && (
        <RollCallView
          open={rollCallOpen}
          onClose={() => setRollCallOpen(false)}
          title={`${state.name} · ${state.code}`}
          subtitle="Every member's personal rejoin link. Hand the phone around and let each friend scan their card."
          participants={state.members}
          linkBase={`${window.location.origin}/contest/${state.code}`}
        />
      )}

      {isHost && (
        <ManageMembersDialog
          open={manageMembersOpen}
          onOpenChange={setManageMembersOpen}
          state={state}
          hostPid={me.id}
          onChanged={onChanged}
        />
      )}
    </div>
  );
};

const StatRow = ({ state }) => (
  <div className="grid sm:grid-cols-3 gap-3">
    <StatChip
      icon={<DollarSign className="w-4 h-4" />}
      label="Salary cap"
      value={fmtMoney(state.salary_cap)}
      testId="contest-stat-cap"
    />
    <StatChip
      icon={<ListChecks className="w-4 h-4" />}
      label="Roster size"
      value={state.roster_size}
      testId="contest-stat-roster"
    />
    <StatChip
      icon={<Calendar className="w-4 h-4" />}
      label="Picks lock"
      value={fmtLockTime(state.lock_at)}
      testId="contest-stat-lock"
    />
  </div>
);

const StatChip = ({ icon, label, value, testId }) => (
  <div
    className="bg-white border border-pbo-border rounded-2xl px-4 py-3 flex items-center gap-3"
    data-testid={testId}
  >
    <span className="w-9 h-9 rounded-full bg-pbo-green/10 text-pbo-green grid place-items-center">
      {icon}
    </span>
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-pbo-muted">
        {label}
      </div>
      <div className="font-serif text-pbo-green text-lg leading-tight">{value}</div>
    </div>
  </div>
);

const RosterPanel = ({ state, me }) => {
  const total = state.members.length;
  const submitted = state.members.filter((m) => m.has_submitted).length;
  const paid = state.members.filter((m) => m.paid).length;
  const isHost = me?.is_host;
  return (
    <div className="bg-white border border-pbo-border rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-serif text-pbo-green text-xl">In the room</h3>
        <span className="text-xs text-pbo-muted">{total}</span>
      </div>
      {state.status === "open" && total > 0 && (
        <div className="mb-3 text-[11px] text-pbo-muted flex flex-wrap gap-x-3">
          <span>
            {submitted}/{total} submitted
          </span>
          <span data-testid="roster-paid-summary">
            {paid}/{total} paid
          </span>
        </div>
      )}
      <ul className="space-y-1.5" data-testid="contest-members-list">
        {state.members.map((m) => (
          <li
            key={m.id}
            className="flex items-center justify-between text-sm py-1.5 px-2 rounded hover:bg-pbo-paper"
          >
            <span className="flex items-center gap-2 min-w-0">
              <span className="w-1.5 h-1.5 rounded-full bg-pbo-green shrink-0" />
              <span className="text-pbo-ink truncate">{m.username}</span>
              {m.paid && (
                <span
                  className="text-[10px] uppercase tracking-wide font-semibold text-pbo-green shrink-0 bg-pbo-green/10 px-1.5 py-0.5 rounded"
                  title="Marked paid by host"
                  data-testid={`member-paid-${m.id}`}
                >
                  $ Paid
                </span>
              )}
              {state.status === "open" && m.has_submitted && (
                <span
                  className="text-[10px] uppercase tracking-wide font-semibold text-pbo-green-soft shrink-0"
                  title="Picks submitted"
                  data-testid={`member-submitted-${m.id}`}
                >
                  ✓ Ready
                </span>
              )}
            </span>
            <span className="flex items-center gap-1.5 shrink-0">
              {isHost && me?.id !== m.id && (
                <SeatLinkQR
                  url={`${window.location.origin}/contest/${state.code}?as=${m.id}`}
                  username={m.username}
                  testId={`contest-seat-qr-${m.id}`}
                  variant="light"
                />
              )}
              {m.is_host && (
                <span className="text-[10px] uppercase tracking-wide font-semibold text-pbo-gold">
                  Host
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const GolferPool = ({
  state,
  isHost,
  hostPid,
  me,
  localPicks,
  setLocalPicks,
  editingPicks,
  onChanged,
}) => {
  const [name, setName] = useState("");
  const [salary, setSalary] = useState("");
  const [adding, setAdding] = useState(false);

  const myPicks = localPicks || [];
  const mySalaryUsed = useMemo(() => {
    const map = {};
    for (const g of state.golfers) map[g.id] = g.salary;
    return myPicks.reduce((sum, id) => sum + (map[id] || 0), 0);
  }, [myPicks, state.golfers]);
  const canPick = state.status === "open" && !!me && editingPicks;

  const sorted = useMemo(
    () => [...state.golfers].sort((a, b) => b.salary - a.salary),
    [state.golfers]
  );

  const onAdd = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    const sal = parseInt(salary, 10);
    if (!trimmed) {
      toast.error("Name required");
      return;
    }
    if (isNaN(sal) || sal < 0) {
      toast.error("Salary must be a non-negative number");
      return;
    }
    setAdding(true);
    try {
      await addContestGolfer(state.code, hostPid, trimmed, sal);
      setName("");
      setSalary("");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add golfer");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="bg-white border border-pbo-border rounded-2xl p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-pbo-green text-2xl">The field</h2>
          <p className="text-xs text-pbo-muted mt-0.5">
            {state.golfers.length} golfer{state.golfers.length === 1 ? "" : "s"}
            {isHost && " · click any row to edit"}
          </p>
        </div>
        {canPick && (
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${
              mySalaryUsed > state.salary_cap
                ? "border-red-300 bg-red-50"
                : mySalaryUsed >= state.salary_cap * 0.9
                ? "border-pbo-gold/40 bg-pbo-gold/10"
                : "border-pbo-green/30 bg-pbo-green/5"
            }`}
            data-testid="field-remaining-chip"
          >
            <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-pbo-muted">
              Remaining
            </span>
            <span
              className={`font-mono font-bold text-base ${
                mySalaryUsed > state.salary_cap
                  ? "text-red-600"
                  : "text-pbo-green"
              }`}
            >
              {fmtMoney(state.salary_cap - mySalaryUsed)}
            </span>
            <span className="text-[10px] text-pbo-muted">
              · {myPicks.length}/{state.roster_size}
            </span>
          </div>
        )}
      </div>

      {isHost && state.status === "open" && (
        <ContestListsBar
          state={state}
          hostPid={hostPid}
          onChanged={onChanged}
        />
      )}

      {isHost && (
        <form
          onSubmit={onAdd}
          className="grid grid-cols-[1fr_auto_auto] gap-2 mb-5"
          data-testid="contest-add-golfer-form"
        >
          <Input
            placeholder="Golfer name (e.g. Steve)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="bg-pbo-paper"
            data-testid="contest-golfer-name-input"
          />
          <div className="relative">
            <DollarSign className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-pbo-muted" />
            <Input
              type="number"
              min={0}
              step="any"
              placeholder="Salary"
              value={salary}
              onChange={(e) => setSalary(e.target.value)}
              className="bg-pbo-paper pl-7 w-32"
              data-testid="contest-golfer-salary-input"
            />
          </div>
          <Button
            type="submit"
            disabled={adding}
            className="bg-pbo-green hover:bg-pbo-green-deep text-pbo-cream rounded-full px-4"
            data-testid="contest-add-golfer-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        </form>
      )}

      {state.golfers.length === 0 ? (
        <div className="text-center py-10 text-pbo-muted text-sm border border-dashed border-pbo-border rounded-xl">
          {isHost
            ? "Add your first golfer above — or open Edit settings → Scoring → PBO live points to import the tournament roster."
            : "The host hasn't added any golfers yet."}
        </div>
      ) : (
        <div className="divide-y divide-pbo-border/60" data-testid="contest-golfer-list">
          {sorted.map((g) => (
            <GolferRow
              key={g.id}
              g={g}
              isHost={isHost}
              code={state.code}
              hostPid={hostPid}
              me={me}
              canPick={canPick}
              picked={myPicks.includes(g.id)}
              myPicks={myPicks}
              mySalaryUsed={mySalaryUsed}
              rosterSize={state.roster_size}
              salaryCap={state.salary_cap}
              setLocalPicks={setLocalPicks}
              onChanged={onChanged}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const GolferRow = ({
  g,
  isHost,
  code,
  hostPid,
  me,
  canPick,
  picked,
  myPicks,
  mySalaryUsed,
  rosterSize,
  salaryCap,
  setLocalPicks,
  onChanged,
}) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(g.name);
  const [salary, setSalary] = useState(String(g.salary));
  const [busy, setBusy] = useState(false);

  const togglePick = () => {
    if (!me || !canPick) return;
    if (picked) {
      setLocalPicks((prev) => prev.filter((p) => p !== g.id));
      return;
    }
    if (myPicks.length >= rosterSize) {
      toast.error(`Roster is full (max ${rosterSize}). Drop someone first.`);
      return;
    }
    if (mySalaryUsed + g.salary > salaryCap) {
      toast.error(
        `Adding ${g.name} would put you over the cap by $${(
          mySalaryUsed + g.salary - salaryCap
        ).toLocaleString()}.`
      );
      return;
    }
    setLocalPicks((prev) => [...prev, g.id]);
  };

  const cancel = () => {
    setEditing(false);
    setName(g.name);
    setSalary(String(g.salary));
  };

  const save = async () => {
    const trimmed = name.trim();
    const sal = parseInt(salary, 10);
    if (!trimmed) {
      toast.error("Name required");
      return;
    }
    if (isNaN(sal) || sal < 0) {
      toast.error("Salary must be a non-negative number");
      return;
    }
    setBusy(true);
    try {
      await editContestGolfer(code, hostPid, g.id, { name: trimmed, salary: sal });
      setEditing(false);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${g.name} from the pool?`)) return;
    setBusy(true);
    try {
      await removeContestGolfer(code, hostPid, g.id);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove");
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div className="py-3 flex items-center gap-2 flex-wrap">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="flex-1 min-w-[140px] bg-pbo-paper h-9"
          data-testid={`golfer-edit-name-${g.id}`}
        />
        <div className="relative">
          <DollarSign className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-pbo-muted" />
          <Input
            type="number"
            min={0}
            step="any"
            value={salary}
            onChange={(e) => setSalary(e.target.value)}
            className="bg-pbo-paper pl-6 w-28 h-9"
            data-testid={`golfer-edit-salary-${g.id}`}
          />
        </div>
        <Button
          size="sm"
          onClick={save}
          disabled={busy}
          className="bg-pbo-green hover:bg-pbo-green-deep text-pbo-cream rounded-full h-9 px-3"
          data-testid={`golfer-save-${g.id}`}
        >
          <Check className="w-4 h-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={cancel}
          className="rounded-full h-9 px-3"
          data-testid={`golfer-cancel-${g.id}`}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  return (
    <div
      className={`py-3 flex items-center justify-between gap-3 group ${
        picked ? "bg-pbo-gold/10 -mx-3 px-3 rounded" : ""
      }`}
      data-testid={`golfer-row-${g.id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span
          className={`w-2 h-2 rounded-full flex-shrink-0 ${
            picked ? "bg-pbo-green" : "bg-pbo-gold"
          }`}
        />
        <span className="text-pbo-ink truncate">
          <GolferName g={g} testId={`field-golfer-detail-${g.id}`} />
        </span>
        {picked && (
          <span className="text-[10px] uppercase tracking-wide font-semibold text-pbo-green-soft">
            On your team
          </span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <span className="font-mono text-pbo-green-soft font-semibold">
          {fmtMoney(g.salary)}
        </span>
        {canPick && (
          <Button
            onClick={togglePick}
            variant={picked ? "default" : "outline"}
            className={`rounded-full h-11 sm:h-8 px-4 sm:px-3 text-sm sm:text-xs font-semibold ${
              picked
                ? "bg-pbo-green text-pbo-cream hover:bg-pbo-green-deep"
                : "border-pbo-green text-pbo-green hover:bg-pbo-green hover:text-pbo-cream"
            }`}
            data-testid={`pick-toggle-${g.id}`}
          >
            {picked ? (
              <>
                <Check className="w-3.5 h-3.5 mr-1" /> Picked
              </>
            ) : (
              <>
                <Plus className="w-3.5 h-3.5 mr-1" /> Pick
              </>
            )}
          </Button>
        )}
        {isHost && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => setEditing(true)}
              className="p-1.5 rounded hover:bg-pbo-paper text-pbo-muted hover:text-pbo-green"
              title="Edit"
              data-testid={`golfer-edit-${g.id}`}
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={remove}
              className="p-1.5 rounded hover:bg-pbo-paper text-pbo-muted hover:text-red-600"
              title="Remove"
              data-testid={`golfer-remove-${g.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const SettingsDialog = ({ state, hostPid, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(state.name);
  const [cap, setCap] = useState(state.salary_cap);
  const [roster, setRoster] = useState(state.roster_size);
  const [lockLocal, setLockLocal] = useState(toDatetimeLocal(state.lock_at));
  const initialUrls = state.scoring_csv_urls && state.scoring_csv_urls.length
    ? state.scoring_csv_urls
    : (state.scoring_csv_url ? [state.scoring_csv_url] : [""]);
  const [roundsCount, setRoundsCount] = useState(
    state.rounds_count || initialUrls.length || 3
  );
  const [scoringUrls, setScoringUrls] = useState(() => {
    const arr = [...initialUrls];
    const target = state.rounds_count || initialUrls.length || 3;
    while (arr.length < target) arr.push("");
    return arr.slice(0, target);
  });
  const [roundLabels, setRoundLabels] = useState(() => {
    const target = state.rounds_count || initialUrls.length || 3;
    const arr = [...(state.round_labels || [])];
    while (arr.length < target) arr.push(`R${arr.length + 1}`);
    return arr.slice(0, target);
  });
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [entryFee, setEntryFee] = useState(state.entry_fee || 0);
  const [splits, setSplits] = useState(
    [...(state.payout_splits || [60, 30, 10]), 0, 0, 0].slice(0, 3)
  );
  const [source, setSource] = useState(state.scoring_source || "sheets");

  useEffect(() => {
    if (open) {
      setSource(state.scoring_source || "sheets");
      setName(state.name);
      setCap(state.salary_cap);
      setRoster(state.roster_size);
      setLockLocal(toDatetimeLocal(state.lock_at));
      const urls = state.scoring_csv_urls && state.scoring_csv_urls.length
        ? state.scoring_csv_urls
        : (state.scoring_csv_url ? [state.scoring_csv_url] : []);
      const rc = state.rounds_count || urls.length || 3;
      setRoundsCount(rc);
      const arr = [...urls];
      while (arr.length < rc) arr.push("");
      setScoringUrls(arr.slice(0, rc));
      const labels = [...(state.round_labels || [])];
      while (labels.length < rc) labels.push(`R${labels.length + 1}`);
      setRoundLabels(labels.slice(0, rc));
      setEntryFee(state.entry_fee || 0);
      setSplits([...(state.payout_splits || [60, 30, 10]), 0, 0, 0].slice(0, 3));
    }
    // Intentionally only re-init on `open` flip — re-running on every
    // background-poll state change would stomp the user's in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const updateRounds = (n) => {
    const next = Math.max(1, Math.min(10, parseInt(n, 10) || 1));
    setRoundsCount(next);
    setScoringUrls((curr) => {
      const arr = [...curr];
      while (arr.length < next) arr.push("");
      return arr.slice(0, next);
    });
    setRoundLabels((curr) => {
      const arr = [...curr];
      while (arr.length < next) arr.push(`R${arr.length + 1}`);
      return arr.slice(0, next);
    });
  };

  const updateUrlAt = (idx, val) => {
    setScoringUrls((curr) => {
      const next = [...curr];
      next[idx] = val;
      return next;
    });
  };

  const updateLabelAt = (idx, val) => {
    setRoundLabels((curr) => {
      const next = [...curr];
      next[idx] = val;
      return next;
    });
  };

  const save = async () => {
    const fee = parseFloat(entryFee) || 0;
    const splitNums = splits.map((s) => parseFloat(s) || 0);
    if (fee > 0) {
      const sum = splitNums.reduce((a, b) => a + b, 0);
      if (Math.abs(sum - 100) > 0.01) {
        toast.error(`Payout splits must add up to 100% (currently ${sum}%)`);
        return;
      }
    }
    setBusy(true);
    try {
      let lockIso = "";
      if (lockLocal) {
        const d = new Date(lockLocal);
        if (!isNaN(d.getTime())) lockIso = d.toISOString();
      }
      const changes = {
        name: name.trim() || state.name,
        salary_cap: parseInt(cap, 10) || state.salary_cap,
        roster_size: parseInt(roster, 10) || state.roster_size,
        lock_at: lockIso,
        entry_fee: fee,
        payout_splits: splitNums,
      };
      if (source === "pbo" && state.pbo_tournament_id) {
        changes.scoring_source = "pbo";
      } else {
        changes.scoring_source = "sheets";
        changes.scoring_csv_urls = scoringUrls.map((u) => u.trim()).filter(Boolean);
        changes.rounds_count = roundsCount;
        changes.round_labels = roundLabels;
      }
      await updateContestSettings(state.code, hostPid, changes);
      toast.success("Settings saved");
      setOpen(false);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  const testAll = async () => {
    const trimmed = scoringUrls.map((u) => u.trim());
    if (!trimmed.some(Boolean)) {
      toast.error("Add at least one round URL first");
      return;
    }
    setTesting(true);
    try {
      await updateContestSettings(state.code, hostPid, {
        scoring_csv_urls: trimmed.filter(Boolean),
        rounds_count: roundsCount,
        round_labels: roundLabels,
      });
      const res = await refreshContestScores(state.code);
      if (res.ok === false) {
        toast.error(`Sheet error: ${res.error || "unknown"}`);
      } else {
        const perRound = res.scores_round_matched || [];
        const errs = res.scores_round_errors || [];
        const labels = roundLabels;
        const summary = perRound
          .map((n, i) =>
            errs[i]
              ? `${labels[i] || `R${i + 1}`} ✗`
              : `${labels[i] || `R${i + 1}`}: ${n} matched`
          )
          .join(" · ");
        const totalMatched = Object.keys(res.golfer_scores || {}).length;
        if (totalMatched === 0) {
          const preview = (res.scores_sheet_preview || []).slice(0, 5);
          const previewText = preview.length
            ? ` Sheet showed: ${preview.map((p) => `"${p}"`).join(", ")}…`
            : "";
          toast.error(
            `Fetched the sheet, but 0 golfer names matched. Your tab should have Column A = golfer name, Column B = points.${previewText} See Standings panel for full diagnostics.`,
            { duration: 10000 }
          );
        } else {
          toast.success(
            `✓ ${totalMatched} golfer${totalMatched === 1 ? "" : "s"} have scores · ${summary}`
          );
        }
        onChanged();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't test sheets");
    } finally {
      setTesting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          className="bg-pbo-cream/10 hover:bg-pbo-cream/20 text-pbo-cream rounded-full h-10"
          data-testid="contest-settings-btn"
        >
          <Settings2 className="w-4 h-4 mr-1.5" />
          Edit settings
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-pbo-cream max-h-[85vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="font-serif text-pbo-green text-2xl">
            Contest settings
          </DialogTitle>
          <DialogDescription className="text-pbo-ink/70">
            Update name, cap, roster size, lock time, payouts and the live scoring source. Host-only.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-pbo-muted">
              Contest name
            </Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-white" data-testid="settings-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-pbo-muted">
                Salary cap
              </Label>
              <Input
                type="number"
                min={1}
                value={cap}
                onChange={(e) => setCap(e.target.value)}
                className="bg-white"
                data-testid="settings-cap-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-pbo-muted">
                Roster size
              </Label>
              <Input
                type="number"
                min={1}
                max={20}
                value={roster}
                onChange={(e) => setRoster(e.target.value)}
                className="bg-white"
                data-testid="settings-roster-input"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-pbo-muted">
              Lock time
            </Label>
            <Input
              type="datetime-local"
              value={lockLocal}
              onChange={(e) => setLockLocal(e.target.value)}
              className="bg-white"
              data-testid="settings-lock-input"
            />
            <p className="text-[11px] text-pbo-muted">
              Leave blank to lock manually later.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-pbo-muted">
              Payouts — display only, no money handled
            </Label>
            <div className="grid grid-cols-4 gap-2">
              <div>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={entryFee}
                  onChange={(e) => setEntryFee(e.target.value)}
                  className="bg-white"
                  data-testid="settings-entry-fee-input"
                />
                <p className="text-[10px] text-pbo-muted mt-1 text-center">Entry fee $</p>
              </div>
              {splits.map((s, i) => (
                <div key={i}>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={s}
                    onChange={(e) =>
                      setSplits((curr) => {
                        const next = [...curr];
                        next[i] = e.target.value;
                        return next;
                      })
                    }
                    className="bg-white"
                    data-testid={`settings-split-input-${i + 1}`}
                  />
                  <p className="text-[10px] text-pbo-muted mt-1 text-center">
                    {["1st", "2nd", "3rd"][i]} %
                  </p>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-pbo-muted">
              Pot = entry fee × submitted entries. Set the fee to 0 to hide
              payouts entirely.
            </p>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-pbo-muted">
              Scoring source
            </Label>
            <div
              className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-pbo-paper border border-pbo-border"
              data-testid="scoring-source-toggle"
            >
              {[
                ["pbo", "PBO live points"],
                ["sheets", "Google Sheets"],
              ].map(([val, label]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => setSource(val)}
                  className={`h-9 rounded-md text-xs font-semibold transition-colors ${
                    source === val
                      ? "bg-pbo-green text-pbo-cream shadow-sm"
                      : "text-pbo-muted hover:text-pbo-green"
                  }`}
                  data-testid={`scoring-source-${val}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          {source === "pbo" && (
            <PboScoringSection state={state} hostPid={hostPid} onChanged={onChanged} />
          )}
          <div className={`space-y-2 ${source === "pbo" ? "hidden" : ""}`}>
            <div className="flex items-end justify-between gap-3 flex-wrap">
              <Label className="text-xs uppercase tracking-wider text-pbo-muted">
                Scoring sheets — one tab per round
              </Label>
              <div className="flex items-center gap-2">
                <Label className="text-[11px] text-pbo-muted">
                  Number of rounds
                </Label>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={roundsCount}
                  onChange={(e) => updateRounds(e.target.value)}
                  className="bg-white h-8 w-16 text-center"
                  data-testid="settings-rounds-count-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              {scoringUrls.map((url, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    value={roundLabels[idx] || ""}
                    onChange={(e) => updateLabelAt(idx, e.target.value.slice(0, 20))}
                    placeholder={`R${idx + 1}`}
                    className="bg-white text-xs w-20 font-mono uppercase tracking-wider text-center"
                    data-testid={`round-label-input-${idx + 1}`}
                  />
                  <Input
                    value={url}
                    onChange={(e) => updateUrlAt(idx, e.target.value)}
                    placeholder={`${roundLabels[idx] || `R${idx + 1}`} — paste edit URL (#gid=…) for live updates`}
                    className="bg-white text-xs flex-1"
                    data-testid={`settings-scoring-url-input-${idx + 1}`}
                  />
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[11px] text-pbo-muted leading-relaxed flex-1 min-w-0 space-y-1.5">
                <div>
                  <strong className="text-pbo-ink">For instant updates</strong>{" "}
                  (recommended): make sure your sheet is shared as{" "}
                  <strong>Anyone with the link → Viewer</strong>, then paste the{" "}
                  <strong>regular edit URL</strong> with{" "}
                  <code className="text-[10px] bg-pbo-paper px-1 rounded">
                    #gid=...
                  </code>{" "}
                  for each tab. We auto-convert it to a live <code className="text-[10px] bg-pbo-paper px-1 rounded">/export?format=csv</code> read.
                </div>
                <div className="text-pbo-muted/80">
                  <strong>Alt:</strong> publish each tab via{" "}
                  <em>File → Share → Publish to web → CSV</em> — works fine but
                  Google caches it for ~5 minutes between updates.
                </div>
                <div className="text-pbo-muted/80">
                  Either way, each tab must have Column&nbsp;A = golfer name,
                  Column&nbsp;B = points.
                </div>
              </div>
              <Button
                type="button"
                onClick={testAll}
                disabled={testing || !scoringUrls.some((u) => u.trim())}
                variant="outline"
                className="rounded-md h-9 text-xs border-pbo-green text-pbo-green hover:bg-pbo-green hover:text-pbo-cream whitespace-nowrap"
                data-testid="settings-test-sheet-btn"
              >
                {testing ? "Testing…" : "Test all rounds"}
              </Button>
            </div>
            {state.scores_round_matched && state.scores_round_matched.length > 0 && (
              <div className="text-[11px] text-pbo-muted flex flex-wrap gap-2 mt-1">
                Last fetch:
                {state.scores_round_matched.map((n, i) => {
                  const err = (state.scores_round_errors || [])[i];
                  return (
                    <span
                      key={i}
                      className={`px-1.5 py-0.5 rounded ${
                        err
                          ? "bg-red-50 text-red-700"
                          : n > 0
                          ? "bg-pbo-green/10 text-pbo-green-soft"
                          : "bg-pbo-paper text-pbo-muted"
                      }`}
                    >
                      {roundLabels[i] || `R${i + 1}`}: {err ? "✗" : `${n} matched`}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
          <DangerZone state={state} hostPid={hostPid} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={busy}
            className="bg-pbo-green hover:bg-pbo-green-deep text-pbo-cream"
            data-testid="settings-save-btn"
          >
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// --- Danger zone: permanent delete (host only) ---
const DangerZone = ({ state, hostPid }) => {
  const nav = useNavigate();
  const [armed, setArmed] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const canDelete = confirm.trim().toUpperCase() === state.code;
  const doDelete = async () => {
    setBusy(true);
    try {
      await deleteContest(state.code, hostPid);
      clearContestSession(state.code);
      toast.success(`Contest ${state.code} deleted`);
      nav("/my-contests", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete contest");
      setBusy(false);
    }
  };
  return (
    <div className="rounded-xl border border-red-200 bg-red-50/60 p-3 space-y-2" data-testid="danger-zone">
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-red-700">Danger zone</div>
      {!armed ? (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-red-900/80">
            Permanently delete this contest — members, lineups and scores are removed for everyone.
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => setArmed(true)}
            className="h-8 text-xs border-red-300 text-red-700 hover:bg-red-600 hover:text-white"
            data-testid="delete-contest-btn"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete contest
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="text-xs text-red-900">
            Type <strong className="font-mono tracking-widest">{state.code}</strong> to confirm. This cannot be undone.
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={state.code}
              className="h-8 w-32 font-mono uppercase tracking-widest bg-white border-red-300"
              autoFocus
              data-testid="delete-contest-confirm-input"
            />
            <Button
              type="button"
              onClick={doDelete}
              disabled={!canDelete || busy}
              className="h-8 text-xs bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-contest-confirm-btn"
            >
              {busy ? "Deleting…" : "Delete forever"}
            </Button>
            <button type="button" onClick={() => { setArmed(false); setConfirm(""); }} className="text-xs text-pbo-muted hover:text-pbo-ink" data-testid="delete-contest-cancel-btn">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};


// --- PBO live points (partner API) section inside Settings ---
const fmtPboDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const PboScoringSection = ({ state, hostPid, onChanged }) => {
  const [tournaments, setTournaments] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [tid, setTid] = useState(state.pbo_tournament_id || "");
  const [matching, setMatching] = useState(false);
  const meta = state.pbo_meta;
  const linked = !!state.pbo_tournament_id;

  useEffect(() => {
    fetchPboTournaments(state.code, hostPid)
      .then((list) => {
        setTournaments(list);
        if (!tid && list.length) {
          const live = list.find((t) => t.status === "active") || list.find((t) => t.status === "upcoming") || list[0];
          setTid(live.id);
        }
      })
      .catch((e) => {
        setTournaments([]);
        setLoadError(e?.response?.data?.detail || "Couldn't reach PBO");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-3 p-3 rounded-xl bg-white border border-pbo-border" data-testid="pbo-scoring-section">
      {linked && (
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-pbo-muted font-semibold">Linked tournament</div>
            <div className="font-serif text-pbo-green text-lg leading-tight" data-testid="pbo-linked-name">
              {state.pbo_tournament_name}
            </div>
          </div>
          {meta && (
            <div className="text-[11px] text-pbo-muted text-right space-y-0.5" data-testid="pbo-sync-meta">
              <div>
                {meta.finished_rounds?.length || 0}/{meta.counted_rounds?.length || 0} scored rounds closed
              </div>
              <div className={meta.bonus_awarded ? "text-pbo-green-soft font-semibold" : ""}>
                Finish bonus {meta.bonus_awarded ? "awarded ✓" : "pending"}
              </div>
              {meta.generated_at && (
                <div>
                  PBO data <RelTime iso={meta.generated_at} />
                </div>
              )}
              {state.pbo_standings_pushed_at && (
                <div data-testid="pbo-standings-pushed">
                  Teams pushed to PBO app <RelTime iso={state.pbo_standings_pushed_at} />
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {(state.pbo_link_error || state.pbo_standings_error) && (
        <div className="px-2.5 py-1.5 rounded-md bg-amber-50 border border-amber-200 text-[11px] text-amber-800" data-testid="pbo-link-error">
          PBO sync warning: {state.pbo_link_error || state.pbo_standings_error}
        </div>
      )}
      {state.pbo_push_missing_contact?.length > 0 && (
        <div className="px-2.5 py-1.5 rounded-md bg-amber-50 border border-amber-200 text-[11px] text-amber-800" data-testid="pbo-missing-contact">
          No email on file for <strong>{state.pbo_push_missing_contact.join(", ")}</strong> — PBO can&apos;t send
          them a recap. Add their email in <em>Manage members</em>.
        </div>
      )}
      <div className="space-y-1.5">
        <Label className="text-[11px] text-pbo-muted">
          {linked ? "Switch tournament / re-sync roster" : "Pick the PBO tournament"}
        </Label>
        {loadError ? (
          <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-md px-2.5 py-1.5" data-testid="pbo-load-error">
            {loadError}
          </div>
        ) : (
          <div className="flex items-center gap-2 min-w-0">
            <select
              value={tid}
              onChange={(e) => setTid(e.target.value)}
              disabled={!tournaments}
              className="flex-1 min-w-0 w-full h-9 rounded-md border border-pbo-border bg-white px-2 text-sm text-pbo-ink truncate"
              data-testid="settings-pbo-tournament-select"
            >
              {!tournaments && <option value="">Loading tournaments…</option>}
              {tournaments && tournaments.length === 0 && <option value="">No tournaments in PBO yet</option>}
              {(tournaments || []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.display_name || t.name} · {t.status} · {t.players_count} players{t.date ? ` · ${fmtPboDate(t.date)}` : ""}
                </option>
              ))}
            </select>
            <Button
              type="button"
              onClick={() => setMatching(true)}
              disabled={!tid || state.status === "final" || matching}
              className="h-9 text-xs bg-pbo-green hover:bg-pbo-green-deep text-pbo-cream whitespace-nowrap shrink-0"
              data-testid="settings-pbo-link-btn"
            >
              {linked ? "Re-match / re-sync" : state.golfers.length ? "Match golfers" : "Link + import roster"}
            </Button>
          </div>
        )}
      </div>
      {matching && tid && (
        <PboMatchPanel
          state={state}
          hostPid={hostPid}
          tournamentId={tid}
          onDone={() => {
            setMatching(false);
            onChanged();
          }}
          onCancel={() => setMatching(false)}
        />
      )}
      <div className="text-[11px] text-pbo-muted leading-relaxed space-y-1">
        <div>
          Golfers already in your field are <strong>matched</strong> to PBO players (names, salaries and
          submitted lineups untouched); an empty field imports the whole roster with a handicap-based
          starter salary you can edit. Points pull straight from PBO: hole results vs personal par,
          plus the 50/30/15 finish bonus once all rounds close.
        </div>
        <div>
          Registers this room in the PBO app (turns on its <strong>Fantasy Draft</strong> button),
          shares your lock time so the app shows a countdown, and pushes every team&apos;s total so
          members see &quot;Your team 2nd of 12&quot; on their PBO home screen. The contest settles
          itself when PBO marks the tournament complete.
        </div>
      </div>
    </div>
  );
};

// --- Lock countdown ---
const LockCountdown = ({ lockAt }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const ms = new Date(lockAt).getTime() - now;
  if (isNaN(ms)) return null;
  if (ms <= 0) {
    return (
      <p className="text-xs text-pbo-gold mt-1 font-semibold">
        Lock time reached — auto-locking…
      </p>
    );
  }
  const total = Math.floor(ms / 1000);
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const urgent = ms <= 5 * 60 * 1000;
  let txt;
  if (d > 0) txt = `${d}d ${h}h ${m}m`;
  else if (h > 0) txt = `${h}h ${m}m ${s}s`;
  else txt = `${m}m ${s}s`;
  return (
    <p
      className={`text-xs mt-1.5 font-mono ${
        urgent ? "text-pbo-gold animate-pulse" : "text-pbo-cream/70"
      }`}
      data-testid="lock-countdown"
    >
      Picks lock in <strong className="text-pbo-gold">{txt}</strong>
    </p>
  );
};

// --- Pick panel: salary meter + your team (draft until Submit) ---
const TeamNameEditor = ({ state, me, onChanged, variant = "light" }) => {
  const dark = variant === "dark";
  const current = state.members?.find((m) => m.id === me?.id)?.team_name || "";
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(current);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!editing) setVal(current);
  }, [current, editing]);
  const save = async () => {
    setBusy(true);
    try {
      await setTeamName(state.code, me.id, val);
      toast.success(val.trim() ? `Team name set: ${val.trim()}` : "Team name cleared");
      setEditing(false);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save team name");
    } finally {
      setBusy(false);
    }
  };
  if (!me) return null;
  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className={
          dark
            ? "inline-flex items-center gap-2 rounded-full border border-pbo-gold/50 bg-pbo-gold/15 hover:bg-pbo-gold/25 px-3 py-1.5 text-xs text-pbo-cream transition-colors"
            : "inline-flex items-center gap-1.5 text-xs text-pbo-muted hover:text-pbo-green mt-1"
        }
        data-testid="team-name-edit-btn"
      >
        <Pencil className={dark ? "w-3.5 h-3.5 text-pbo-gold" : "w-3 h-3"} />
        {current ? (
          <>
            <span className={dark ? "text-pbo-cream/70" : ""}>Team:</span>{" "}
            <strong className={dark ? "text-pbo-gold" : "text-pbo-green"} data-testid="team-name-display">{current}</strong>
          </>
        ) : (
          <span className={dark ? "font-semibold" : ""}>Name your team</span>
        )}
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2 mt-1.5 flex-wrap" data-testid="team-name-form">
      <Input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        maxLength={40}
        placeholder="e.g. Reilly's Rippers"
        className={`h-8 text-sm max-w-[220px] ${dark ? "bg-pbo-cream text-pbo-ink border-pbo-gold/50" : ""}`}
        autoFocus
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") setEditing(false);
        }}
        data-testid="team-name-input"
      />
      <Button size="sm" onClick={save} disabled={busy} className={`h-8 text-xs ${dark ? "bg-pbo-gold text-pbo-ink hover:bg-pbo-gold-soft" : "bg-pbo-green hover:bg-pbo-green-deep text-pbo-cream"}`} data-testid="team-name-save-btn">
        {busy ? "Saving…" : "Save"}
      </Button>
      <button type="button" onClick={() => setEditing(false)} className={`text-xs ${dark ? "text-pbo-cream/70 hover:text-pbo-cream" : "text-pbo-muted hover:text-pbo-ink"}`} data-testid="team-name-cancel-btn">
        Cancel
      </button>
    </div>
  );
};

const PickPanel = ({
  state,
  me,
  onChanged,
  localPicks,
  setLocalPicks,
  editing,
  submitting,
  onSubmit,
  onStartEdit,
  onCancelEdit,
}) => {
  const submittedPicks = state.my_entry?.picks || [];
  const submitted = !!state.my_entry;
  const cap = state.salary_cap;
  const rosterSize = state.roster_size;

  const golferById = useMemo(() => {
    const m = {};
    for (const g of state.golfers) m[g.id] = g;
    return m;
  }, [state.golfers]);

  const used = useMemo(
    () => localPicks.reduce((s, id) => s + (golferById[id]?.salary || 0), 0),
    [localPicks, golferById]
  );
  const remaining = cap - used;
  const pct = Math.min(100, Math.max(0, (used / cap) * 100));
  const overCap = used > cap;
  const overRoster = localPicks.length > rosterSize;

  const picks = localPicks.map((id) => golferById[id]).filter(Boolean);
  const isComplete = localPicks.length === rosterSize && !overCap;

  // Dirty = local differs from submitted (only meaningful in editing mode)
  const dirty = useMemo(() => {
    if (localPicks.length !== submittedPicks.length) return true;
    const setL = new Set(localPicks);
    return submittedPicks.some((p) => !setL.has(p));
  }, [localPicks, submittedPicks]);

  const removePick = (gid) => {
    if (!editing) return;
    setLocalPicks((prev) => prev.filter((p) => p !== gid));
  };

  return (
    <div
      className="bg-white border border-pbo-border rounded-2xl p-5 sm:p-6"
      data-testid="pick-panel"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 className="font-serif text-pbo-green text-2xl flex items-center gap-2">
            Your team
            {submitted && !editing && (
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-pbo-green/10 text-pbo-green-soft"
                data-testid="picks-submitted-badge"
              >
                <Check className="w-3 h-3" /> Submitted
              </span>
            )}
            {editing && submitted && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-pbo-gold/15 text-pbo-gold">
                Editing
              </span>
            )}
          </h2>
          <p className="text-xs text-pbo-muted mt-0.5">
            {editing ? (
              <>
                Pick {rosterSize} golfer{rosterSize === 1 ? "" : "s"} under the
                cap, then <strong>Submit</strong>. Picks are private until lock.
              </>
            ) : (
              <>Picks are private until lock. You can edit anytime before then.</>
            )}
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-pbo-muted">
            Remaining
          </div>
          <div
            className={`font-serif text-2xl ${
              overCap ? "text-red-600" : "text-pbo-green"
            }`}
            data-testid="pick-remaining"
          >
            {fmtMoney(remaining)}
          </div>
        </div>
      </div>

      {/* Salary meter */}
      <div className="mb-5">
        <div className="flex justify-between items-baseline mb-1">
          <span className="text-xs text-pbo-muted">
            Used <strong className="text-pbo-ink">{fmtMoney(used)}</strong> /{" "}
            {fmtMoney(cap)}
          </span>
          <span className="text-xs text-pbo-muted">
            {localPicks.length} / {rosterSize} picked
          </span>
        </div>
        <div className="h-2.5 rounded-full bg-pbo-paper overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              overCap ? "bg-red-500" : pct >= 90 ? "bg-pbo-gold" : "bg-pbo-green"
            }`}
            style={{ width: `${pct}%` }}
            data-testid="salary-meter-fill"
          />
        </div>
        {overCap && (
          <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5" />
            Over cap by {fmtMoney(used - cap)} — drop someone to save your picks.
          </p>
        )}
      </div>

      {picks.length === 0 ? (
        <div className="text-center py-8 text-pbo-muted text-sm border border-dashed border-pbo-border rounded-xl">
          {editing ? (
            <>
              No picks yet — tap <strong className="text-pbo-green">Pick</strong>{" "}
              on any golfer below to build your team.
            </>
          ) : (
            <>You haven&apos;t submitted any picks yet.</>
          )}
        </div>
      ) : (
        <ul className="space-y-1.5" data-testid="my-picks-list">
          {picks.map((g) => (
            <li
              key={g.id}
              className="flex items-center justify-between px-3 py-2 rounded-lg bg-pbo-paper/70"
              data-testid={`my-pick-${g.id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-1.5 h-1.5 rounded-full bg-pbo-green flex-shrink-0" />
                <GolferName g={g} className="text-pbo-ink truncate" testId={`my-pick-detail-${g.id}`} />
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm text-pbo-green-soft">
                  {fmtMoney(g.salary)}
                </span>
                {editing && (
                  <button
                    onClick={() => removePick(g.id)}
                    className="p-1 rounded hover:bg-white text-pbo-muted hover:text-red-600"
                    title="Drop"
                    data-testid={`drop-pick-${g.id}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Actions */}
      <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
        {editing ? (
          <>
            <div className="text-[11px] text-pbo-muted flex items-center gap-1.5">
              {isComplete ? (
                <>
                  <Check className="w-3.5 h-3.5 text-pbo-green" />
                  <span>Lineup complete — hit Submit to lock it in.</span>
                </>
              ) : (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-pbo-gold" />
                  <span>
                    {localPicks.length === 0
                      ? "Tap Pick below to start."
                      : `Add ${rosterSize - localPicks.length} more for a full lineup (or submit partial).`}
                  </span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              {submitted && (
                <Button
                  variant="ghost"
                  onClick={onCancelEdit}
                  className="rounded-full h-9 text-xs text-pbo-muted hover:text-pbo-ink"
                  data-testid="cancel-edit-picks-btn"
                >
                  Cancel
                </Button>
              )}
              <Button
                onClick={onSubmit}
                disabled={
                  submitting ||
                  overCap ||
                  overRoster ||
                  localPicks.length === 0 ||
                  (submitted && !dirty)
                }
                className="rounded-full h-10 px-5 bg-pbo-green text-pbo-cream hover:bg-pbo-green-deep font-semibold"
                data-testid="submit-picks-btn"
              >
                {submitting
                  ? "Submitting…"
                  : submitted
                  ? "Update picks"
                  : "Submit picks"}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="text-[11px] text-pbo-muted flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5 text-pbo-green" />
              <span>
                {submittedPicks.length} pick{submittedPicks.length === 1 ? "" : "s"}{" "}
                submitted. Need to swap someone? Hit Edit picks.
              </span>
            </div>
            <Button
              onClick={onStartEdit}
              variant="outline"
              className="rounded-full h-10 px-5 border-pbo-green text-pbo-green hover:bg-pbo-green hover:text-pbo-cream font-semibold"
              data-testid="edit-picks-btn"
            >
              <Pencil className="w-3.5 h-3.5 mr-1.5" />
              Edit picks
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

// --- Locked teams + live standings panel ---
const fmtPayout = (n) =>
  `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const teamLabel = (r) => r.team_name || r.username;

const ChampionCard = ({ state }) => {
  const standings = state.standings || [];
  const champs = standings.filter((r) => r.rank === 1);
  if (!champs.length) return null;
  const payouts = state.payout_preview?.payouts || [];
  const runners = standings
    .filter((r) => r.rank === 2 || r.rank === 3)
    .slice(0, 4);
  const share = async () => {
    const lines = [
      `Champion: ${champs.map(teamLabel).join(" & ")} — \u201c${state.name}\u201d with ${champs[0].total_points} pts! 🏆`,
      ...standings
        .slice(0, 3)
        .map(
          (r) =>
            `#${r.rank} ${teamLabel(r)} — ${r.total_points} pts${
              payouts[r.rank - 1] != null ? ` (${fmtPayout(payouts[r.rank - 1])})` : ""
            }`
        ),
      `${window.location.origin}/contest/${state.code}/standings`,
    ];
    const text = lines.join("\n");
    if (navigator.share) {
      try {
        await navigator.share({ text });
        return;
      } catch {
        /* fall through to clipboard */
      }
    }
    await navigator.clipboard.writeText(text);
    toast.success("Recap copied — paste it in the group chat");
  };
  return (
    <div
      className="mb-5 rounded-2xl bg-pbo-green text-pbo-cream p-5 sm:p-6 relative overflow-hidden"
      data-testid="champion-card"
    >
      <div className="absolute inset-0 pbo-stripe opacity-20 pointer-events-none" />
      <div className="relative">
        <div className="text-[10px] uppercase tracking-[0.3em] text-pbo-gold font-semibold">
          Final results
          {state.finalized_at
            ? ` · ${new Date(state.finalized_at).toLocaleDateString()}`
            : ""}
        </div>
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          <span className="w-12 h-12 rounded-full bg-pbo-gold text-pbo-ink grid place-items-center shrink-0">
            <Trophy className="w-6 h-6" />
          </span>
          <div className="min-w-0">
            <div
              className="font-serif text-2xl sm:text-3xl leading-tight"
              data-testid="champion-name"
            >
              {champs.map(teamLabel).join(" & ")}
            </div>
            <div className="text-pbo-cream/80 text-sm">
              {champs.some((c) => c.team_name) ? `${champs.map((c) => c.username).join(" & ")} · ` : ""}
              Champion · {champs[0].total_points} pts
              {payouts[0] != null ? (
                <span className="text-pbo-gold font-semibold">
                  {" "}
                  · wins {fmtPayout(payouts[0])}
                </span>
              ) : null}
            </div>
          </div>
          <Button
            onClick={share}
            className="ml-auto bg-pbo-gold text-pbo-ink hover:bg-pbo-gold-soft rounded-full h-9 text-xs font-semibold shrink-0"
            data-testid="share-recap-btn"
          >
            Share recap
          </Button>
        </div>
        {runners.length > 0 && (
          <div className="mt-4 flex gap-2 flex-wrap">
            {runners.map((r) => (
              <span
                key={r.participant_id}
                className="text-xs bg-pbo-cream/10 rounded-full px-3 py-1.5"
                data-testid={`runner-up-${r.participant_id}`}
              >
                #{r.rank} {teamLabel(r)} · {r.total_points} pts
                {payouts[r.rank - 1] != null
                  ? ` · ${fmtPayout(payouts[r.rank - 1])}`
                  : ""}
              </span>
            ))}
          </div>
        )}
        <div className="mt-3 text-xs text-pbo-cream/70">
          Winning lineup: {champs[0].picks.map((p) => p.name).join(", ")}
        </div>
      </div>
    </div>
  );
};

const LockedTeamsPanel = ({ state, me, onChanged }) => {
  const standings = state.standings || [];
  const entries = state.entries || [];
  const golferById = useMemo(() => {
    const m = {};
    for (const g of state.golfers) m[g.id] = g;
    return m;
  }, [state.golfers]);
  // Ownership % per golfer = drafters who picked this golfer / total drafters
  // with a submitted lineup (only available post-lock since backend gates the
  // standings array on status === locked).
  const ownershipByGolfer = useMemo(() => {
    const totalDrafters = standings.length;
    const counts = {};
    for (const r of standings) {
      for (const p of r.picks) {
        counts[p.golfer_id] = (counts[p.golfer_id] || 0) + 1;
      }
    }
    const out = {};
    for (const [gid, count] of Object.entries(counts)) {
      out[gid] = {
        count,
        total: totalDrafters,
        pct: totalDrafters > 0 ? Math.round((count / totalDrafters) * 100) : 0,
      };
    }
    return out;
  }, [standings]);
  const hasScoringUrl = !!state.has_scoring;
  const isPbo = state.scoring_source === "pbo";
  const [refreshing, setRefreshing] = useState(false);
  const isHost = me?.is_host;
  const isFinal = state.status === "final";
  const payouts = state.payout_preview?.payouts || [];

  const onFinalize = async () => {
    if (
      !window.confirm(
        "Finalize results? Standings freeze at their current values and the champion is crowned. You can reopen scoring later if needed."
      )
    )
      return;
    try {
      await finalizeContest(state.code, me.id);
      toast.success("Results finalized — congrats to the champion! 🏆");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't finalize");
    }
  };

  const onReopen = async () => {
    try {
      await reopenContestScoring(state.code, me.id);
      toast.success("Scoring reopened");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't reopen");
    }
  };

  const onRefresh = async () => {
    if (!hasScoringUrl) {
      toast.error("Host needs to set a scoring source first (Edit settings).");
      return;
    }
    setRefreshing(true);
    try {
      const res = await refreshContestScores(state.code);
      if (res.ok === false) {
        toast.error(`Couldn't fetch scores: ${res.error || "unknown error"}`);
      } else {
        toast.success("Scores refreshed");
      }
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const publicLink = `${window.location.origin}/contest/${state.code}/standings`;
  const copyLink = () =>
    navigator.clipboard
      .writeText(publicLink)
      .then(() => toast.success("Standings link copied"));

  return (
    <div
      className="bg-white border border-pbo-border rounded-2xl p-5 sm:p-6"
      data-testid="locked-teams-panel"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-pbo-gold" />
          <h2 className="font-serif text-pbo-green text-2xl">Standings</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isFinal && (
            <Button
              onClick={onRefresh}
              disabled={refreshing}
              variant="outline"
              className="rounded-full h-9 text-xs border-pbo-green text-pbo-green hover:bg-pbo-green hover:text-pbo-cream"
              data-testid="refresh-scores-btn"
            >
              {refreshing ? "Refreshing…" : "🔄 Refresh scores"}
            </Button>
          )}
          {isHost && !isFinal && (
            <Button
              onClick={onFinalize}
              className="rounded-full h-9 text-xs bg-pbo-gold text-pbo-ink hover:bg-pbo-gold-soft font-semibold"
              data-testid="finalize-contest-btn"
            >
              <Trophy className="w-3.5 h-3.5 mr-1.5" />
              Finalize results
            </Button>
          )}
          {isHost && isFinal && (
            <Button
              onClick={onReopen}
              variant="ghost"
              className="rounded-full h-9 text-xs text-pbo-muted hover:text-pbo-green"
              data-testid="reopen-scoring-btn"
            >
              Reopen scoring
            </Button>
          )}
          <Button
            onClick={copyLink}
            variant="outline"
            className="rounded-full h-9 text-xs border-pbo-gold text-pbo-gold hover:bg-pbo-gold hover:text-pbo-ink"
            data-testid="copy-standings-link-btn"
          >
            Copy public link
          </Button>
        </div>
      </div>

      {isFinal && <ChampionCard state={state} />}

      {state.payout_preview && (
        <div
          className="mb-4 px-4 py-3 rounded-xl bg-pbo-gold/10 border border-pbo-gold/30 flex items-center gap-x-3 gap-y-2 flex-wrap"
          data-testid="payout-preview"
        >
          <span className="font-serif text-pbo-green text-lg leading-none">
            Pot {fmtPayout(state.payout_preview.pot)}
          </span>
          <span className="text-[11px] text-pbo-muted">
            {state.payout_preview.entries_count}{" "}
            {state.payout_preview.entries_count === 1 ? "entry" : "entries"} ×{" "}
            {fmtPayout(state.payout_preview.entry_fee)}
          </span>
          <span className="flex gap-1.5 flex-wrap sm:ml-auto">
            {state.payout_preview.payouts.map((p, i) => (
              <span
                key={i}
                className="text-xs font-semibold bg-white border border-pbo-gold/40 text-pbo-gold px-2.5 py-1 rounded-full"
                data-testid={`payout-place-${i + 1}`}
              >
                {["1st", "2nd", "3rd", "4th", "5th"][i] || `${i + 1}th`}{" "}
                {fmtPayout(p)}
              </span>
            ))}
          </span>
        </div>
      )}
      {!hasScoringUrl && isHost && (
        <div className="mb-4 px-3 py-2 rounded-lg bg-pbo-gold/10 border border-pbo-gold/30 text-xs text-pbo-ink leading-relaxed">
          <strong>Set up scoring:</strong> open <em>Edit settings</em> and either
          link the PBO tournament (live points, no spreadsheet) or paste a Google
          Sheets link (Golfer Name, Points). Scores auto-refresh every 30 seconds.
        </div>
      )}
      {isPbo && state.pbo_meta && (
        <div
          className="mb-4 px-3 py-2 rounded-lg bg-pbo-green/5 border border-pbo-green/20 text-[11px] text-pbo-ink flex flex-wrap gap-x-3 gap-y-1 items-center"
          data-testid="pbo-live-strip"
        >
          <span className="font-semibold text-pbo-green">PBO live · {state.pbo_tournament_name}</span>
          <span className="text-pbo-muted">
            {state.pbo_meta.finished_rounds?.length || 0}/{state.pbo_meta.counted_rounds?.length || 0} rounds closed
          </span>
          <span className={state.pbo_meta.bonus_awarded ? "text-pbo-green-soft font-semibold" : "text-pbo-muted"}>
            Finish bonus {state.pbo_meta.bonus_awarded ? "awarded ✓" : "pending"}
          </span>
          {state.pbo_stream?.connected && state.status === "locked" && (
            <span className="inline-flex items-center gap-1 text-pbo-green font-semibold" data-testid="pbo-stream-live">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Instant updates on
            </span>
          )}
          {state.finalized_by === "pbo" && (
            <span className="text-pbo-gold font-semibold" data-testid="pbo-auto-settled">Settled automatically by PBO</span>
          )}
        </div>
      )}
      {state.scores_error && (
        <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">
          Last fetch error: {state.scores_error}
        </div>
      )}
      {state.scores_unmatched_names && state.scores_unmatched_names.length > 0 && isHost && (
        <div className="mb-4 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
          <strong>Heads up:</strong>{" "}
          {isPbo
            ? "these PBO players aren't in your field yet — re-sync the roster in Edit settings to add them:"
            : "these names from your sheet didn't match any golfer in the pool — check spelling:"}{" "}
          {state.scores_unmatched_names.join(", ")}
        </div>
      )}
      {Object.keys(state.golfer_scores || {}).length === 0 &&
        state.scores_checked_at &&
        !state.scores_error &&
        !isPbo &&
        isHost && (
          <div
            className="mb-4 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800 space-y-1"
            data-testid="zero-matched-banner"
          >
            <div>
              <strong>Fetched the sheet, but 0 golfers got scores.</strong>{" "}
              Your tab needs Column&nbsp;A = golfer name, Column&nbsp;B = points.
            </div>
            {state.scores_sheet_preview && state.scores_sheet_preview.length > 0 && (
              <div className="text-amber-700">
                Names found in your sheet:{" "}
                {state.scores_sheet_preview
                  .slice(0, 8)
                  .map((p) => `"${p}"`)
                  .join(", ")}
                {state.scores_sheet_preview.length > 8 ? "…" : ""}
              </div>
            )}
            <div className="text-amber-700">
              Easiest fix: add a small <em>LiveScores</em> tab in your sheet
              with golfer names down column A and fantasy-points totals down
              column B (use cell references to your scorecards). Publish that
              tab and paste its CSV link here.
            </div>
          </div>
        )}
      {state.scores_updated_at && !isFinal && (
        <p className="text-[11px] text-pbo-muted mb-4">
          Scores updated{" "}
          <RelTime iso={state.scores_updated_at} /> · auto-refreshes every 30
          seconds
        </p>
      )}
      {isFinal && (
        <p className="text-[11px] text-pbo-muted mb-4" data-testid="final-frozen-note">
          Final — scores are frozen.
        </p>
      )}

      {entries.length === 0 ? (
        <div className="text-center py-8 text-pbo-muted text-sm border border-dashed border-pbo-border rounded-xl">
          No teams were submitted before lock.
        </div>
      ) : (
        <div className="space-y-2" data-testid="standings-list">
          {standings.map((row) => (
            <StandingsRow
              key={row.participant_id}
              row={row}
              isMe={me?.id === row.participant_id}
              golferById={golferById}
              roundLabels={state.round_labels || []}
              ownershipByGolfer={ownershipByGolfer}
              payout={payouts[row.rank - 1]}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const StandingsRow = ({ row, isMe, golferById, roundLabels = [], ownershipByGolfer = {}, payout }) => {
  const [open, setOpen] = useState(false);
  const hasAnyScore = row.picks.some((p) => p.points !== null && p.points !== undefined);
  const roundTotals = row.round_totals || [];
  return (
    <div
      className={`border rounded-xl overflow-hidden transition-colors ${
        isMe ? "border-pbo-gold bg-pbo-gold/5" : "border-pbo-border"
      }`}
      data-testid={`standings-row-${row.participant_id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-pbo-paper/60 text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-8 flex flex-col items-center shrink-0">
            <span
              className={`font-mono font-bold text-lg leading-tight ${
                row.rank === 1 ? "text-pbo-gold" : "text-pbo-muted"
              }`}
            >
              #{row.rank}
            </span>
            {row.movement > 0 && (
              <span
                className="text-[10px] font-bold text-emerald-600 leading-none"
                title={`Up ${row.movement} since last update`}
                data-testid={`move-up-${row.participant_id}`}
              >
                ▲{row.movement}
              </span>
            )}
            {row.movement < 0 && (
              <span
                className="text-[10px] font-bold text-red-600 leading-none"
                title={`Down ${-row.movement} since last update`}
                data-testid={`move-down-${row.participant_id}`}
              >
                ▼{-row.movement}
              </span>
            )}
          </span>
          <div className="min-w-0">
            <div className="font-serif text-pbo-green text-lg leading-tight truncate">
              {row.team_name || row.username}
              {isMe && (
                <span className="ml-2 text-[10px] uppercase tracking-wide text-pbo-gold font-semibold">
                  You
                </span>
              )}
            </div>
            <div className="text-[11px] text-pbo-muted">
              {row.team_name ? `${row.username} · ` : ""}
              {row.picks.length} picks · {fmtMoney(row.total_salary)}
            </div>
            {hasAnyScore && roundTotals.length > 0 && (
              <div className="flex gap-1.5 mt-1 flex-wrap">
                {roundTotals.map((rt, i) => (
                  <span
                    key={i}
                    className={`text-[10px] font-mono px-1.5 py-0.5 rounded font-semibold ${
                      rt > 0
                        ? "bg-pbo-green/10 text-pbo-green-soft"
                        : rt < 0
                        ? "bg-red-50 text-red-700"
                        : "bg-pbo-paper text-pbo-muted"
                    }`}
                    data-testid={`row-round-${row.participant_id}-${i + 1}`}
                  >
                    {roundLabels[i] || `R${i + 1}`} {rt > 0 ? `+${rt}` : rt}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="text-right">
          <div
            className={`font-serif text-2xl ${
              row.total_points > 0
                ? "text-pbo-green"
                : row.total_points < 0
                ? "text-red-600"
                : "text-pbo-ink"
            }`}
            data-testid={`standings-points-${row.participant_id}`}
          >
            {hasAnyScore ? row.total_points : "—"}
          </div>
          <div className="text-[10px] uppercase tracking-wide text-pbo-muted font-semibold">
            pts
          </div>
          {payout != null && (
            <div
              className="text-[11px] font-bold text-pbo-gold mt-0.5"
              data-testid={`row-payout-${row.participant_id}`}
            >
              ${Number(payout).toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </div>
          )}
        </div>
      </button>
      {open && (
        <div className="border-t border-pbo-border/50 bg-pbo-paper/40">
          {/* ────────────────────────────────────────────────────────────
             MOBILE (< sm) — stacked card per golfer
             ──────────────────────────────────────────────────────────── */}
          <ul className="sm:hidden px-3 py-3 space-y-2">
            {row.picks.map((p) => {
              const totalCls =
                p.points == null
                  ? "text-pbo-muted"
                  : p.points > 0
                  ? "text-pbo-green-soft"
                  : p.points < 0
                  ? "text-red-600"
                  : "text-pbo-ink";
              return (
                <li
                  key={p.golfer_id}
                  className="bg-white rounded-lg border border-pbo-border/60 px-3 py-2.5"
                  data-testid={`standings-pick-${row.participant_id}-${p.golfer_id}`}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-pbo-ink text-sm font-medium truncate flex items-center gap-1.5">
                        <GolferName g={p} className="truncate" testId={`standings-golfer-mobile-${row.participant_id}-${p.golfer_id}`} />
                        {ownershipByGolfer[p.golfer_id]?.count === 1 &&
                          ownershipByGolfer[p.golfer_id]?.total > 1 && (
                            <span
                              className="text-pbo-gold shrink-0"
                              title="Unique pick — only this drafter has this golfer"
                              aria-label="Unique pick"
                              data-testid={`unique-mobile-${p.golfer_id}`}
                            >
                              ★
                            </span>
                          )}
                      </div>
                      {ownershipByGolfer[p.golfer_id] && (
                        <div
                          className="text-[10px] text-pbo-muted mt-0.5"
                          data-testid={`ownership-mobile-${p.golfer_id}`}
                        >
                          {ownershipByGolfer[p.golfer_id].pct}% owned
                          {ownershipByGolfer[p.golfer_id].count === 1 &&
                            ownershipByGolfer[p.golfer_id].total > 1 && (
                              <span className="text-pbo-gold font-semibold ml-1">
                                · Unique
                              </span>
                            )}
                        </div>
                      )}
                    </div>
                    <span
                      className={`font-mono font-bold text-base shrink-0 tabular-nums ${totalCls}`}
                    >
                      {p.points == null ? "—" : p.points}
                      <span className="text-[10px] uppercase tracking-wider text-pbo-muted font-semibold ml-1">
                        pts
                      </span>
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <span className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded bg-pbo-paper text-pbo-muted">
                      {fmtMoney(p.salary)}
                    </span>
                    {(p.round_points || []).map((rp, i) => {
                      const cls =
                        rp == null
                          ? "bg-pbo-paper text-pbo-muted/70"
                          : rp > 0
                          ? "bg-pbo-green/10 text-pbo-green-soft"
                          : rp < 0
                          ? "bg-red-50 text-red-600"
                          : "bg-pbo-paper text-pbo-ink";
                      return (
                        <span
                          key={i}
                          className={`text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded uppercase tracking-wider ${cls}`}
                        >
                          {roundLabels[i] || `R${i + 1}`}{" "}
                          {rp == null ? "—" : rp > 0 ? `+${rp}` : rp}
                        </span>
                      );
                    })}
                    {p.bonus > 0 && (
                      <span
                        className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded uppercase tracking-wider bg-pbo-gold/20 text-pbo-ink"
                        data-testid={`bonus-mobile-${p.golfer_id}`}
                      >
                        Bonus +{p.bonus}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          {/* ────────────────────────────────────────────────────────────
             DESKTOP (sm+) — original tabular layout
             ──────────────────────────────────────────────────────────── */}
          <div className="hidden sm:block">
            {/* Header row */}
            <div className="px-4 pt-2.5 pb-1 grid grid-cols-[minmax(0,1fr)_auto_auto_auto] gap-3 items-baseline text-[10px] uppercase tracking-[0.15em] font-semibold text-pbo-muted">
              <span>Golfer</span>
              <span className="text-right w-14">Salary</span>
              <span className="flex gap-2">
                {(row.picks[0]?.round_points || []).map((_, i) => (
                  <span key={i} className="w-9 text-center">
                    {roundLabels[i] || `R${i + 1}`}
                  </span>
                ))}
              </span>
              <span className="text-right w-12">Total</span>
            </div>
            {/* Rows */}
            <ul className="px-4 pb-3">
              {row.picks.map((p) => (
                <li
                  key={p.golfer_id}
                  className="grid grid-cols-[minmax(0,1fr)_auto_auto_auto] gap-3 items-center text-sm py-1"
                >
                  <div className="min-w-0">
                    <div className="text-pbo-ink truncate flex items-center gap-1.5">
                      <GolferName g={p} className="truncate" testId={`standings-golfer-desktop-${row.participant_id}-${p.golfer_id}`} />
                      {ownershipByGolfer[p.golfer_id]?.count === 1 &&
                        ownershipByGolfer[p.golfer_id]?.total > 1 && (
                          <span
                            className="text-pbo-gold shrink-0"
                            title="Unique pick — only this drafter has this golfer"
                            aria-label="Unique pick"
                            data-testid={`unique-desktop-${p.golfer_id}`}
                          >
                            ★
                          </span>
                        )}
                    </div>
                    {ownershipByGolfer[p.golfer_id] && (
                      <div
                        className="text-[10px] text-pbo-muted leading-tight"
                        data-testid={`ownership-desktop-${p.golfer_id}`}
                      >
                        {ownershipByGolfer[p.golfer_id].pct}% owned
                        {ownershipByGolfer[p.golfer_id].count === 1 &&
                          ownershipByGolfer[p.golfer_id].total > 1 && (
                            <span className="text-pbo-gold font-semibold ml-1">
                              · Unique
                            </span>
                          )}
                      </div>
                    )}
                  </div>
                  <span className="font-mono text-xs text-pbo-muted text-right w-14">
                    {fmtMoney(p.salary)}
                  </span>
                  <span className="flex gap-2">
                    {(p.round_points || []).map((rp, i) => (
                      <span
                        key={i}
                        className={`font-mono text-xs w-9 text-center tabular-nums ${
                          rp == null
                            ? "text-pbo-muted/60"
                            : rp > 0
                            ? "text-pbo-green-soft"
                            : rp < 0
                            ? "text-red-600"
                            : "text-pbo-ink"
                        }`}
                        title={`Round ${i + 1}`}
                      >
                        {rp == null ? "—" : rp}
                      </span>
                    ))}
                    {p.bonus > 0 && (
                      <span
                        className="font-mono text-[10px] text-pbo-ink bg-pbo-gold/20 rounded px-1 self-center"
                        title="Finish bonus"
                        data-testid={`bonus-desktop-${p.golfer_id}`}
                      >
                        +{p.bonus}
                      </span>
                    )}
                  </span>
                  <span
                    className={`font-mono font-semibold w-12 text-right tabular-nums ${
                      p.points == null
                        ? "text-pbo-muted"
                        : p.points > 0
                        ? "text-pbo-green-soft"
                        : p.points < 0
                        ? "text-red-600"
                        : "text-pbo-ink"
                    }`}
                  >
                    {p.points == null ? "—" : p.points}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

const RelTime = ({ iso }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  if (!iso) return null;
  const t = new Date(iso).getTime();
  const diffSec = Math.max(0, Math.floor((now - t) / 1000));
  let label;
  if (diffSec < 30) label = "just now";
  else if (diffSec < 60) label = `${diffSec}s ago`;
  else if (diffSec < 3600) label = `${Math.floor(diffSec / 60)}m ago`;
  else if (diffSec < 86400) label = `${Math.floor(diffSec / 3600)}h ago`;
  else label = `${Math.floor(diffSec / 86400)}d ago`;
  return <span>{label}</span>;
};


// --- Saved golfer lists bar (host-only, pre-lock) ---
const ContestListsBar = ({ state, hostPid, onChanged }) => {
  const [names, setNames] = useState(() => getContestListNames());
  const [saveOpen, setSaveOpen] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);
  const [editingList, setEditingList] = useState(null); // list name being edited
  const [listName, setListName] = useState("");
  const [busy, setBusy] = useState(false);

  const refreshNames = () => setNames(getContestListNames());

  const onSave = () => {
    const trimmed = listName.trim();
    if (!trimmed) {
      toast.error("Give the list a name");
      return;
    }
    if (state.golfers.length === 0) {
      toast.error("Add some golfers first");
      return;
    }
    const existing = getContestList(trimmed);
    if (existing && !window.confirm(`Overwrite saved list "${trimmed}"?`)) return;
    try {
      saveContestList(
        trimmed,
        state.golfers.map((g) => ({ name: g.name, salary: g.salary }))
      );
      toast.success(
        `Saved "${trimmed}" — ${state.golfers.length} golfer${
          state.golfers.length === 1 ? "" : "s"
        }`
      );
      refreshNames();
      setSaveOpen(false);
      setListName("");
    } catch (err) {
      toast.error(err?.message || "Couldn't save");
    }
  };

  const onLoad = async (name, mode) => {
    const list = getContestList(name);
    if (!list || list.length === 0) {
      toast.error("That list is empty");
      return;
    }
    setLoadOpen(false);
    setBusy(true);
    try {
      const existingNames = new Set(
        state.golfers.map((g) => g.name.toLowerCase())
      );
      let added = 0;
      let skipped = 0;
      for (const g of list) {
        if (mode === "merge" && existingNames.has(g.name.toLowerCase())) {
          skipped++;
          continue;
        }
        try {
          await addContestGolfer(state.code, hostPid, g.name, g.salary);
          added++;
        } catch {
          skipped++;
        }
      }
      toast.success(
        `Loaded "${name}" — added ${added}${
          skipped > 0 ? `, skipped ${skipped} duplicate${skipped === 1 ? "" : "s"}` : ""
        }`
      );
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const onDeleteList = (name) => {
    if (!window.confirm(`Delete saved list "${name}"?`)) return;
    deleteContestList(name);
    refreshNames();
    toast.success(`Deleted "${name}"`);
  };

  return (
    <div
      className="flex items-center gap-2 flex-wrap mb-4 pb-4 border-b border-dashed border-pbo-border/60"
      data-testid="contest-lists-bar"
    >
      <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-pbo-muted mr-1">
        Saved lists
      </span>
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={state.golfers.length === 0}
            className="rounded-full h-8 text-xs border-pbo-green text-pbo-green hover:bg-pbo-green hover:text-pbo-cream"
            data-testid="save-list-btn"
          >
            <Save className="w-3.5 h-3.5 mr-1.5" />
            Save current list
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-pbo-cream sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-pbo-green text-xl">
              Save list
            </DialogTitle>
            <DialogDescription className="text-pbo-ink/70">
              Name this golfer list so you can reuse it in future contests.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-pbo-ink/70">
              Save the current {state.golfers.length} golfer
              {state.golfers.length === 1 ? "" : "s"} with their salaries to
              your device so you can reuse them in future contests.
            </p>
            <Input
              autoFocus
              placeholder="List name (e.g. PBO 2026 Field)"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSave();
              }}
              className="bg-white"
              data-testid="save-list-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={onSave}
              className="bg-pbo-green text-pbo-cream hover:bg-pbo-green-deep"
              data-testid="save-list-confirm-btn"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={loadOpen} onOpenChange={setLoadOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            className="rounded-full h-8 text-xs border-pbo-gold text-pbo-gold hover:bg-pbo-gold hover:text-pbo-ink"
            data-testid="load-list-btn"
          >
            <FolderOpen className="w-3.5 h-3.5 mr-1.5" />
            {names.length > 0 ? "Load saved list" : "Saved lists"}
            <ChevronDown className="w-3 h-3 ml-1" />
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-pbo-cream sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-pbo-green text-xl">
              Load a saved list
            </DialogTitle>
            <DialogDescription className="text-pbo-ink/70">
              Add golfers from a previously saved list to this contest&apos;s pool. Duplicates are skipped.
            </DialogDescription>
          </DialogHeader>
          {names.length === 0 ? (
            <p className="text-sm text-pbo-muted py-4 text-center">
              You haven&apos;t saved any lists yet.
            </p>
          ) : (
            <ul className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="saved-lists-list">
              {names.map((nm) => {
                const list = getContestList(nm) || [];
                return (
                  <li
                    key={nm}
                    className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white border border-pbo-border hover:border-pbo-gold transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-serif text-pbo-green truncate">{nm}</div>
                      <div className="text-[11px] text-pbo-muted">
                        {list.length} golfer{list.length === 1 ? "" : "s"}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Button
                        size="sm"
                        onClick={() => onLoad(nm, "merge")}
                        className="h-8 text-xs rounded-full bg-pbo-green text-pbo-cream hover:bg-pbo-green-deep"
                        data-testid={`load-list-merge-${nm}`}
                      >
                        Add to pool
                      </Button>
                      <button
                        onClick={() => {
                          setLoadOpen(false);
                          setEditingList(nm);
                        }}
                        className="p-1.5 rounded hover:bg-pbo-paper text-pbo-muted hover:text-pbo-green"
                        title="Edit saved list"
                        data-testid={`edit-list-${nm}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => onDeleteList(nm)}
                        className="p-1.5 rounded hover:bg-pbo-paper text-pbo-muted hover:text-red-600"
                        title="Delete saved list"
                        data-testid={`delete-list-${nm}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <p className="text-[11px] text-pbo-muted mt-2">
            Lists are saved on this device. Duplicates by name are skipped.
          </p>

          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-pbo-border/60">
            <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-pbo-muted">
              Move between devices
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={names.length === 0}
              onClick={() => {
                const json = exportContestListsJson();
                const blob = new Blob([json], { type: "application/json" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `pbo-saved-lists-${new Date()
                  .toISOString()
                  .slice(0, 10)}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                toast.success("Downloaded .json with all your lists");
              }}
              className="h-8 text-xs rounded-full border-pbo-green text-pbo-green hover:bg-pbo-green hover:text-pbo-cream"
              data-testid="export-lists-btn"
            >
              Export
            </Button>
            <label
              className="h-8 text-xs rounded-full border border-pbo-gold text-pbo-gold hover:bg-pbo-gold hover:text-pbo-ink px-3 inline-flex items-center cursor-pointer"
              data-testid="import-lists-label"
            >
              Import
              <input
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    const text = await file.text();
                    importContestListsJson(text, { merge: true });
                    toast.success("Lists imported");
                    refreshNames();
                  } catch (err) {
                    toast.error(err?.message || "Couldn't import — file may not be valid JSON");
                  } finally {
                    // Reset so re-selecting the same file still fires
                    e.target.value = "";
                  }
                }}
                data-testid="import-lists-file-input"
              />
            </label>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setLoadOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {names.length > 0 && (
        <span className="text-[11px] text-pbo-muted ml-auto">
          {names.length} saved
        </span>
      )}

      {editingList && (
        <EditListDialog
          listName={editingList}
          open={!!editingList}
          onClose={() => setEditingList(null)}
          onSaved={() => {
            refreshNames();
            setEditingList(null);
          }}
        />
      )}
    </div>
  );
};

// --- Edit a saved list (rename, change names/salaries, add/remove) ---
const EditListDialog = ({ listName, open, onClose, onSaved }) => {
  const [name, setName] = useState(listName);
  const [items, setItems] = useState(() =>
    (getContestList(listName) || []).map((g, i) => ({
      key: `${i}-${g.name}`,
      name: g.name,
      salary: String(g.salary),
    }))
  );
  const [newName, setNewName] = useState("");
  const [newSalary, setNewSalary] = useState("");

  const updateItem = (key, field, val) =>
    setItems((prev) =>
      prev.map((it) => (it.key === key ? { ...it, [field]: val } : it))
    );
  const removeItem = (key) =>
    setItems((prev) => prev.filter((it) => it.key !== key));

  const addItem = () => {
    const nm = newName.trim();
    const sal = parseInt(newSalary, 10);
    if (!nm) {
      toast.error("Name required");
      return;
    }
    if (isNaN(sal) || sal < 0) {
      toast.error("Salary must be non-negative");
      return;
    }
    if (items.some((it) => it.name.trim().toLowerCase() === nm.toLowerCase())) {
      toast.error(`${nm} is already in this list`);
      return;
    }
    setItems((prev) => [
      ...prev,
      { key: `new-${Date.now()}`, name: nm, salary: String(sal) },
    ]);
    setNewName("");
    setNewSalary("");
  };

  const onSaveAll = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("List name required");
      return;
    }
    // Validate items
    const cleaned = [];
    for (const it of items) {
      const nm = it.name.trim();
      if (!nm) {
        toast.error("Each golfer needs a name");
        return;
      }
      const sal = parseInt(it.salary, 10);
      if (isNaN(sal) || sal < 0) {
        toast.error(`Invalid salary for ${nm}`);
        return;
      }
      cleaned.push({ name: nm, salary: sal });
    }
    // If rename, do it first
    if (trimmedName !== listName) {
      const ok = renameContestList(listName, trimmedName);
      if (!ok) {
        toast.error(`A list named "${trimmedName}" already exists`);
        return;
      }
    }
    try {
      saveContestList(trimmedName, cleaned);
      toast.success(
        `Saved "${trimmedName}" — ${cleaned.length} golfer${
          cleaned.length === 1 ? "" : "s"
        }`
      );
      onSaved();
    } catch (err) {
      toast.error(err?.message || "Couldn't save");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-pbo-cream sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-pbo-green text-xl">
            Edit saved list
          </DialogTitle>
          <DialogDescription className="text-pbo-ink/70">
            Rename the list and adjust golfer names or salaries. Changes save to this device only.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-pbo-muted">
              List name
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-white"
              data-testid="edit-list-name-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-pbo-muted">
              Golfers ({items.length})
            </Label>
            <div
              className="space-y-1.5 max-h-64 overflow-y-auto pr-1"
              data-testid="edit-list-items"
            >
              {items.length === 0 && (
                <div className="text-xs text-pbo-muted text-center py-4 border border-dashed border-pbo-border rounded">
                  No golfers in this list yet — add some below.
                </div>
              )}
              {items.map((it) => (
                <div key={it.key} className="flex items-center gap-2">
                  <Input
                    value={it.name}
                    onChange={(e) => updateItem(it.key, "name", e.target.value)}
                    className="bg-white h-9 text-sm flex-1"
                    data-testid={`edit-list-row-name-${it.key}`}
                  />
                  <div className="relative">
                    <DollarSign className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-pbo-muted" />
                    <Input
                      type="number"
                      min={0}
                      step="any"
                      value={it.salary}
                      onChange={(e) => updateItem(it.key, "salary", e.target.value)}
                      className="bg-white h-9 text-sm pl-6 w-28"
                      data-testid={`edit-list-row-salary-${it.key}`}
                    />
                  </div>
                  <button
                    onClick={() => removeItem(it.key)}
                    className="p-1.5 rounded hover:bg-pbo-paper text-pbo-muted hover:text-red-600"
                    title="Remove"
                    data-testid={`edit-list-row-remove-${it.key}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-2 border-t border-pbo-border/60">
            <Label className="text-xs uppercase tracking-wider text-pbo-muted mb-1.5 block">
              Add golfer
            </Label>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") addItem();
                }}
                className="bg-white h-9 text-sm flex-1"
                data-testid="edit-list-add-name-input"
              />
              <div className="relative">
                <DollarSign className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-pbo-muted" />
                <Input
                  type="number"
                  min={0}
                  step="any"
                  placeholder="Salary"
                  value={newSalary}
                  onChange={(e) => setNewSalary(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") addItem();
                  }}
                  className="bg-white h-9 text-sm pl-6 w-28"
                  data-testid="edit-list-add-salary-input"
                />
              </div>
              <Button
                onClick={addItem}
                size="sm"
                className="h-9 rounded-full bg-pbo-green text-pbo-cream hover:bg-pbo-green-deep"
                data-testid="edit-list-add-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={onSaveAll}
            className="bg-pbo-green text-pbo-cream hover:bg-pbo-green-deep"
            data-testid="edit-list-save-btn"
          >
            Save list
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
